// AllTimeTablePage.js — /all-time-table: the Premier League table for any
// span of seasons (all-time by default; ?from=&to= in the URL), sortable by
// any column, filterable to this season's or former clubs and by name.
// Computed in the browser from the API's compact match list
// (components/clubs/league.js). The week-by-week race chart is on /race.

import React, { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Skeleton } from "@mui/material";
import SEO from "./components/SEO";
import AdSlot from "./components/AdSlot";
import { CtaRow, H2, P, PageHeader, ProsePage } from "./components/Prose";
import { BRAND } from "./theme";
import { useSeededQuery } from "./components/scorelines/data";
import { ClubSearch } from "./components/clubs/ClubFilters";
import { SeasonBar, rangeLabelOf, useLeagueParams } from "./components/clubs/LeagueControls";
import { MATCHES_QUERY, parseMatches, tableFor } from "./components/clubs/league";
import { POINT_DEDUCTIONS, clubInfo, seasonText } from "./components/clubs/data";
import LeagueTable, { LEAGUE_COLUMNS } from "./components/clubs/LeagueTable";
import { seasonPath } from "./components/clubs/seasons";

const SITE_URL = "https://premierleaguescorigami.com";
export default function AllTimeTablePage() {
  const resp = useSeededQuery(MATCHES_QUERY);
  const data = useMemo(() => (resp.data ? parseMatches(resp.data) : null), [resp.data]);

  return (
    <ProsePage wide>
      <SEO
        title="All-time Premier League table"
        description="The all-time Premier League table since 1992/93, or the table for any season or run of seasons: points, wins, goals, goal difference and points per game."
        path="/all-time-table"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "All-time table", item: `${SITE_URL}/all-time-table` },
          ],
        }}
      />
      {resp.error ? (
        <>
          <PageHeader eyebrow="All-time table" title="All-time Premier League table" />
          <P>The table couldn't be loaded right now. Please try again shortly.</P>
        </>
      ) : !data ? (
        <>
          <PageHeader eyebrow="All-time table" title="All-time Premier League table" />
          {Array.from({ length: 12 }).map((_, i) => (
            <Skeleton key={i} height={40} sx={{ bgcolor: "rgba(255,255,255,0.06)" }} />
          ))}
        </>
      ) : (
        <LeagueView data={data} />
      )}
      <CtaRow
        links={[
          { to: "/clubs", label: "Find your club", primary: true },
          { to: "/", label: "Explore the grid" },
          { to: "/records", label: "Records" },
        ]}
      />
    </ProsePage>
  );
}

function LeagueView({ data }) {
  const lp = useLeagueParams(data);
  const { first, last, from, to } = lp;
  const [sort, setSort] = useState({ key: "pos", desc: false });
  const [status, setStatus] = useState("all");
  const [query, setQuery] = useState("");

  const allTime = useMemo(() => tableFor(data, first, last), [data, first, last]);
  const table = useMemo(() => tableFor(data, from, to), [data, from, to]);
  const isCurrent = (t) => t.lastSeasonOverall === data.currentSeason;
  const byStatus = (t) => (status === "all" ? true : status === "current" ? isCurrent(t) : !isCurrent(t));
  const rangeLabel = rangeLabelOf(from, to, first, last);
  const counts = { all: table.length, current: table.filter(isCurrent).length, former: table.filter((t) => !isCurrent(t)).length };

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const col = LEAGUE_COLUMNS.find((c) => c.key === sort.key);
    return table
      .filter(byStatus)
      .filter((t) => !q || clubInfo(t.team).name.toLowerCase().includes(q) || clubInfo(t.team).short.toLowerCase().includes(q))
      .sort((a, b) => {
        const va = col.sort(a);
        const vb = col.sort(b);
        const cmp = col.text ? String(va).localeCompare(String(vb)) : va - vb;
        return (sort.desc ? -cmp : cmp) || a.pos - b.pos;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, sort, status, query]);
  const onSort = (col) =>
    setSort((s) => (s.key === col.key ? { key: col.key, desc: !s.desc } : { key: col.key, desc: !(col.text || col.asc) }));

  const leader = allTime[0];
  const bestPpg = allTime.filter((t) => t.seasons >= 5).sort((a, b) => b.ppg - a.ppg)[0];
  const lede =
    `Every club's combined record across all ${leader.seasons} Premier League seasons since 1992/93, or any season or run of seasons you choose. ` +
    `${clubInfo(leader.team).name} top the all-time table with ${leader.pts.toLocaleString()} points; ` +
    `${bestPpg.team === leader.team ? "they also have" : `${clubInfo(bestPpg.team).name} have`} the best points per game, ${bestPpg.ppg.toFixed(2)}.`;
  const deductions = POINT_DEDUCTIONS.filter((d) => d.season >= from && d.season <= to);
  const raceLink = `/race?${new URLSearchParams({ ...(from !== first && { from }), ...(to !== last && { to }), clubs: table.slice(0, 6).map((t) => clubInfo(t.team).slug).join(",") })}`;

  return (
    <>
      <Box sx={{ maxWidth: 820 }}>
        <PageHeader eyebrow="All-time table" title="All-time Premier League table" lede={lede} />
      </Box>
      <SeasonBar data={data} lp={lp} status={status} onStatus={setStatus} counts={counts} />

      {/* The heading, its links and the search sit level on one line (wrapping on phones). */}
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 2, rowGap: 1.25, mt: { xs: 4, sm: 5 }, mb: 1.5 }}>
        <H2 id="table" sx={{ mt: 0, mb: 0 }}>
          {rangeLabel === "All-time" ? "All-time table" : `${rangeLabel} table`}
        </H2>
        <Box
          component={RouterLink}
          to={raceLink}
          sx={{ color: BRAND.cyan, fontWeight: 700, fontSize: 14.5, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
        >
          Watch {rangeLabel === "All-time" ? "it" : rangeLabel} as a race →
        </Box>
        {from === to && (
          <Box
            component={RouterLink}
            to={seasonPath(from)}
            sx={{ color: BRAND.cyan, fontWeight: 700, fontSize: 14.5, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
          >
            The {rangeLabel} season →
          </Box>
        )}
        <Box sx={{ ml: { sm: "auto" }, width: { xs: "100%", sm: "auto" } }}>
          <ClubSearch id="table-search" value={query} onChange={setQuery} />
        </Box>
      </Box>
      <LeagueTable rows={rows} sort={sort} onSort={onSort} isCurrent={isCurrent} />
      {rows.length === 0 && <P>No clubs match "{query}".</P>}

      <Box component="p" sx={{ color: BRAND.textSecondary, fontSize: 13.5, lineHeight: 1.7, maxWidth: 820, mt: 1, mb: 3 }}>
        Points are three for a win and one for a draw
        {deductions.length ? (
          <>
            , minus the Premier League's point deductions (*):{" "}
            {deductions.map((d, i) => (
              <React.Fragment key={`${d.team}-${d.season}`}>
                {i > 0 && (i === deductions.length - 1 ? " and " : ", ")}
                {clubInfo(d.team).name} −{d.points} in {seasonText(d.season)} ({d.reason})
              </React.Fragment>
            ))}
            .
          </>
        ) : (
          "."
        )}
      </Box>

      <AdSlot position="records" />
    </>
  );
}
